import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Heart, Compass, Feather, Sun, Moon } from "lucide-react";
import SEO from "../components/SEO";
import HtcMasterMatrix from "../components/HtcMasterMatrix";
import HtcLeadMagnetForm from "../components/HtcLeadMagnetForm";

const CODES = [
  { n: 1,  label: "Wesenscode",       q: "Wer bin ich ursprünglich?",                         d: "Der Wesenscode zeigt die natürliche Energie, Entscheidungsweise, innere Mechanik und ursprüngliche Gabe eines Menschen." },
  { n: 2,  label: "Herkunftscode",    q: "Was trage ich aus meinem Familiensystem?",           d: "Der Herkunftscode macht sichtbar, welche familiären Prägungen, Rollen, Loyalitäten und unausgesprochenen Aufträge noch heute wirken." },
  { n: 3,  label: "Namenscode",       q: "Welche Identität wirkt durch meinen Namen?",         d: "Der Namenscode betrachtet die Verbindung zwischen Name, Identität, Familienrolle, Selbstbild und innerer Resonanz." },
  { n: 4,  label: "Schutzcode",       q: "Wie habe ich gelernt zu überleben?",                 d: "Der Schutzcode zeigt, welche Strategien früher geholfen haben, heute aber Entwicklung, Nähe, Freiheit oder Sichtbarkeit blockieren können." },
  { n: 5,  label: "Schattencode",     q: "Was wurde verdrängt und wirkt im Untergrund?",       d: "Der Schattencode zeigt verdrängte Gefühle, Trigger, ungelebte Wahrheit und gebundene Kraft." },
  { n: 6,  label: "Herzcode",         q: "Wie liebe ich und wo wurde Liebe verletzt?",         d: "Der Herzcode macht sichtbar, wo Liebe frei fließt und wo sie mit Angst, Kontrolle, Leistung oder Bedürftigkeit verwechselt wurde." },
  { n: 7,  label: "Glaubenscode",     q: "Welche inneren Sätze erschaffen meine Realität?",    d: "Der Glaubenscode zeigt die inneren Überzeugungen über Liebe, Geld, Erfolg, Körper, Selbstwert, Beziehungen und Leben." },
  { n: 8,  label: "Körpercode",       q: "Was sagt mein Körper, was mein Kopf nicht hören will?", d: "Der Körpercode versteht Körpersignale, Spannung, Erschöpfung, Überreizung und emotionale Speicherung als wichtige Botschaften." },
  { n: 9,  label: "Beziehungscode",   q: "Welchen alten Schmerz spiegelt mir das Du?",         d: "Der Beziehungscode zeigt wiederkehrende Muster in Partnerschaft, Nähe, Distanz, Projektion, Retterrollen und Selbstwert." },
  { n: 10, label: "Berufungscode",    q: "Welche Gabe will durch mich wirken?",                d: "Der Berufungscode zeigt Talente, natürliche Wirkung, Sichtbarkeit, Lebensaufgabe und den Beitrag, der durch einen Menschen entstehen möchte." },
  { n: 11, label: "Bewusstseinscode", q: "Wie viel Wahrheit und Verantwortung kann ich halten?", d: "Der Bewusstseinscode zeigt die Fähigkeit zur Selbstbeobachtung, Eigenverantwortung, Projektionsrücknahme und inneren Reife." },
  { n: 12, label: "Vermächtniscode",  q: "Welche Frucht soll mein Leben hinterlassen?",        d: "Der Vermächtniscode führt über persönliche Transformation hinaus in Dienst, Generativität, Beitrag, Familie, Gemeinschaft und Herzbewegung." },
];

const MOVEMENTS = [
  { name: "Decode", d: "Ich erkenne den alten Code." },
  { name: "Feel",   d: "Ich fühle, was bisher verdrängt wurde." },
  { name: "Recode", d: "Ich entscheide mich neu." },
  { name: "Embody", d: "Ich verkörpere den neuen Code." },
  { name: "Serve",  d: "Ich mache meine Transformation zum Beitrag." },
];

const RAEUME = [
  { name: "Spiegelraum",   d: "Das Leben zeigt mir meine Muster.",                          icon: Compass },
  { name: "Tiefenraum",    d: "Der Körper öffnet die Wahrheit.",                            icon: Feather },
  { name: "Wandlungsraum", d: "Der alte Code wird gewürdigt und neu ausgerichtet.",         icon: Moon },
  { name: "Wirkungsraum",  d: "Die Transformation wird Beitrag.",                           icon: Sun },
];

const DREI_CODES = [
  {
    name: "Fremdcode",
    kicker: "Übernommen",
    d: "Der Fremdcode besteht aus allem, was der Mensch übernommen hat: Erwartungen, Familienmuster, gesellschaftliche Prägung, alte Rollen, Schuld, Scham und Anpassung."
  },
  {
    name: "Schutzcode",
    kicker: "Überlebensstrategie",
    d: "Der Schutzcode ist das alte Überlebenssystem. Kontrolle, Rückzug, Retterrolle, Anpassung, Härte oder Selbstsabotage waren vielleicht einmal Schutz – können heute aber zum Gefängnis werden."
  },
  {
    name: "Ursprungscode",
    kicker: "Essenz",
    d: "Der Ursprungscode ist die ursprüngliche Essenz des Menschen: Würde, Liebe, Wahrheit, Körperweisheit, Bewusstsein, Berufung und innerer Auftrag."
  },
];

export default function SatHtc() {
  return (
    <div className="min-h-screen text-white bg-[#081328]" data-testid="sat-htc-page">
      <SEO
        title="SAT-HTC™ · Human Transformation Code — SAT Akademie"
        description="SAT-HTC™ ist das Transformationssystem der SAT Akademie: Vom Fremdcode zum Ursprungscode. 12 Codes, 5 Bewegungen, 4 Räume. Human Transformation Code von Nenad Vladović."
        path="/sat-htc"
      />

      {/* ── HERO ─────────────────────────────────────────────────── */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="/brand/htc/mastermatrix.png"
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover opacity-[0.18] blur-[2px]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#081328] via-[#081328]/85 to-[#081328]"></div>
        </div>
        <div className="container-sat relative z-10 max-w-4xl">
          <div className="flex items-center gap-3 mb-8">
            <span className="w-12 h-px bg-[#D4AF37]"></span>
            <span className="overline">SAT-HTC™ · Human Transformation Code</span>
          </div>
          <h1 className="serif font-light leading-[1.05] text-5xl md:text-7xl lg:text-8xl">
            Du bist nicht falsch.
            <span className="block mt-2 italic text-[#D4AF37]">Du bist verschlüsselt.</span>
          </h1>
          <p className="serif italic text-xl md:text-2xl text-white/70 mt-10 max-w-2xl leading-relaxed">
            SAT-HTC™ ist das Transformationssystem der SAT Akademie. Es hilft dir, den inneren
            Code hinter deinen Mustern, Beziehungen, Blockaden, Körperbotschaften und deiner
            Berufung zu erkennen.
          </p>
          <div className="mt-12 flex flex-col sm:flex-row gap-4">
            <Link to="/sat-master" className="btn-primary" data-testid="htc-hero-cta-master">
              Meinen Weg beginnen <ArrowRight size={16} />
            </Link>
            <Link to="/erstgespraech" className="btn-secondary" data-testid="htc-hero-cta-contact">
              Klarheitsgespräch anfragen
            </Link>
          </div>
        </div>
      </section>

      {/* ── WAS BEDEUTET HTC ─────────────────────────────────────── */}
      <section className="py-28 bg-[#050B1B]">
        <div className="container-sat grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="overline mb-6">Grundverständnis</div>
            <h2 className="serif text-4xl md:text-5xl font-light leading-tight">
              Der Mensch ist kein Problem.
              <span className="block italic text-[#D4AF37] mt-2">Er ist ein lebendiger Code.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-white/75 text-lg leading-relaxed">
            <p>
              Jeder Mensch lebt aus einem inneren Code. Dieser Code entsteht aus Herkunft,
              Prägung, Schutzmustern, Glaubenssätzen, Beziehungserfahrungen, Körpererinnerungen,
              verdrängten Emotionen, Werten, Bewusstsein, Berufung und der Sehnsucht nach Liebe
              und Sinn.
            </p>
            <p className="serif italic text-xl text-white/85">
              Wenn dieser Code unbewusst bleibt, wiederholt der Mensch alte Muster. Wenn er
              sichtbar wird, kann Transformation beginnen.
            </p>
            <div className="pt-4 flex items-center gap-3">
              <span className="w-8 h-px bg-[#D4AF37]/60"></span>
              <span className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37]/80">
                Vom Fremdcode zum Ursprungscode
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ── DREI CODES ───────────────────────────────────────────── */}
      <section className="py-28 bg-[#081328]">
        <div className="container-sat">
          <div className="max-w-2xl mb-16">
            <div className="overline mb-4">Die drei Codes</div>
            <h2 className="serif text-4xl md:text-5xl font-light leading-tight">
              Drei Ebenen des inneren Codes.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {DREI_CODES.map((c, i) => (
              <div
                key={c.name}
                className="glass rounded-3xl p-8 border border-white/[0.06] hover:border-[#D4AF37]/40 transition group"
                data-testid={`htc-drei-codes-${i}`}
              >
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]/70 mb-4">
                  {String(i + 1).padStart(2, "0")} · {c.kicker}
                </div>
                <div className="serif text-3xl font-light mb-4 group-hover:text-[#D4AF37] transition">
                  {c.name}
                </div>
                <p className="text-white/65 leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── MASTERMATRIX SVG ──────────────────────────────────────── */}
      <section className="py-28 bg-[#050B1B] relative overflow-hidden">
        <div className="container-sat">
          <div className="max-w-3xl mb-16">
            <div className="overline mb-4">Die SAT-HTC Mastermatrix</div>
            <h2 className="serif text-4xl md:text-5xl font-light leading-tight">
              Ein Blick auf den ganzen Menschen.
            </h2>
            <p className="text-white/65 mt-6 text-lg leading-relaxed max-w-2xl">
              Im Zentrum steht <span className="text-[#D4AF37] italic">Love Is Transformation</span>.
              Darum entfalten sich fünf Bewegungen — Decode, Feel, Recode, Embody, Serve —
              und zwölf Codes, die den inneren Aufbau eines Menschen sichtbar machen.
            </p>
          </div>
          <HtcMasterMatrix size={720} />
        </div>
      </section>

      {/* ── 12 CODES DETAIL ──────────────────────────────────────── */}
      <section className="py-28 bg-[#081328]">
        <div className="container-sat">
          <div className="max-w-2xl mb-16">
            <div className="overline mb-4">Die 12 Codes menschlicher Transformation</div>
            <h2 className="serif text-4xl md:text-5xl font-light leading-tight">
              Zwölf Ebenen, ein Mensch.
            </h2>
            <p className="text-white/65 mt-6 text-lg leading-relaxed">
              SAT-HTC liest den Menschen über zwölf Ebenen. Sie zeigen, wo Prägung wirkt,
              wo Schutz entstanden ist, wo Schatten gebundene Kraft enthält und wo Berufung
              auf Verkörperung wartet.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {CODES.map((c) => (
              <details
                key={c.n}
                className="group rounded-2xl border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04] transition"
                data-testid={`htc-code-${c.n}`}
              >
                <summary className="cursor-pointer list-none p-6 flex items-start gap-5">
                  <span
                    className="serif text-3xl font-light shrink-0 leading-none w-10"
                    style={{ color: "#D4AF37" }}
                  >
                    {String(c.n).padStart(2, "0")}
                  </span>
                  <div className="flex-1">
                    <div className="serif text-xl md:text-2xl text-white group-hover:text-[#D4AF37] transition">
                      {c.label}
                    </div>
                    <div className="serif italic text-white/55 text-sm md:text-base mt-1">
                      „{c.q}&ldquo;
                    </div>
                  </div>
                  <span className="text-white/40 text-xs uppercase tracking-widest mt-2 group-open:hidden">
                    Mehr
                  </span>
                  <span className="text-[#D4AF37] text-xs uppercase tracking-widest mt-2 hidden group-open:inline">
                    Zu
                  </span>
                </summary>
                <div className="px-6 pb-6 pt-2 pl-[4.75rem] text-white/70 leading-relaxed text-[15px]">
                  {c.d}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── 5 BEWEGUNGEN ─────────────────────────────────────────── */}
      <section className="py-28 bg-[#050B1B]">
        <div className="container-sat">
          <div className="max-w-2xl mb-16">
            <div className="overline mb-4">Die 5 Transformationsbewegungen</div>
            <h2 className="serif text-4xl md:text-5xl font-light leading-tight">
              Von Erkenntnis zu Verkörperung.
            </h2>
          </div>
          <div className="grid md:grid-cols-5 gap-4 relative">
            {/* Connecting line */}
            <div className="hidden md:block absolute left-[8%] right-[8%] top-[38px] h-px bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent"></div>
            {MOVEMENTS.map((m, i) => (
              <div key={m.name} className="text-center relative" data-testid={`htc-movement-${i}`}>
                <div className="relative z-10 mx-auto w-16 h-16 rounded-full bg-[#081328] border-2 border-[#D4AF37] flex items-center justify-center serif text-[#D4AF37] text-lg">
                  {i + 1}
                </div>
                <div className="serif text-2xl mt-6 text-white" style={{ letterSpacing: "0.06em" }}>
                  {m.name}
                </div>
                <p className="serif italic text-white/60 text-sm mt-3 leading-relaxed px-2">
                  {m.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 4 RÄUME ──────────────────────────────────────────────── */}
      <section className="py-28 bg-[#081328]">
        <div className="container-sat">
          <div className="max-w-2xl mb-16">
            <div className="overline mb-4">Die 4 Räume</div>
            <h2 className="serif text-4xl md:text-5xl font-light leading-tight">
              Räume, in denen Transformation geschieht.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {RAEUME.map((r, i) => {
              const Ico = r.icon;
              return (
                <div
                  key={r.name}
                  className="rounded-3xl p-10 border border-white/[0.06] bg-gradient-to-br from-white/[0.02] to-transparent"
                  data-testid={`htc-raum-${i}`}
                >
                  <Ico size={22} className="text-[#D4AF37] mb-6" />
                  <div className="serif text-3xl font-light mb-3">{r.name}</div>
                  <p className="serif italic text-white/70 text-lg leading-relaxed">{r.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── SAT-HTC × ANGEBOTE ───────────────────────────────────── */}
      <section className="py-28 bg-[#050B1B]">
        <div className="container-sat">
          <div className="max-w-2xl mb-16">
            <div className="overline mb-4">SAT-HTC und die SAT Angebote</div>
            <h2 className="serif text-4xl md:text-5xl font-light leading-tight">
              Ein Herzsystem, drei Wege.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                to: "/sat-master",
                title: "SAT MASTER",
                sub: "Der eigene Code",
                d: "Im SAT MASTER wird der eigene Code erkannt, verstanden und über mehrere Monate in Alltag, Beziehung, Körper, Werte und Berufung integriert.",
                cta: "Zum SAT MASTER",
              },
              {
                to: "/mentor",
                title: "SAT MENTOR",
                sub: "Den Code lesen lernen",
                d: "Im SAT MENTOR lernen Teilnehmer, den Human Transformation Code anderer Menschen verantwortungsvoll zu lesen und Transformation professionell zu begleiten.",
                cta: "Zum SAT MENTOR",
              },
              {
                to: "/herzbewegung",
                title: "HERZBEWEGUNG",
                sub: "Liebe wird praktisch",
                d: "In der Herzbewegung wird Transformation praktisch. Liebe wird zu Handlung, Dienst und Beitrag.",
                cta: "Zur Herzbewegung",
              },
            ].map((k, i) => (
              <Link
                key={k.title}
                to={k.to}
                className="group rounded-3xl p-8 border border-[#D4AF37]/20 hover:border-[#D4AF37]/60 bg-white/[0.02] hover:bg-white/[0.04] transition flex flex-col"
                data-testid={`htc-angebot-${i}`}
              >
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]/70 mb-4">{k.sub}</div>
                <div className="serif text-3xl mb-4 font-light">{k.title}</div>
                <p className="text-white/70 leading-relaxed mb-8 flex-1">{k.d}</p>
                <span className="inline-flex items-center gap-2 text-sm text-[#D4AF37] group-hover:translate-x-1 transition">
                  {k.cta} <ArrowRight size={14} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── VERANTWORTUNG ────────────────────────────────────────── */}
      <section className="py-20 bg-[#081328]">
        <div className="container-sat max-w-3xl">
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8">
            <div className="overline mb-4">Verantwortung & Grenzen</div>
            <p className="text-white/70 leading-relaxed text-[15px]">
              SAT-HTC ersetzt keine medizinische, psychiatrische oder psychotherapeutische
              Behandlung. Es ist ein Bewusstseins- und Transformationssystem zur Selbstreflexion,
              Integration und persönlichen Entwicklung. Bei schweren psychischen, traumatischen
              oder medizinischen Themen wird ergänzende fachliche Unterstützung empfohlen.
            </p>
          </div>
        </div>
      </section>

      {/* ── LEAD MAGNET: SAT-HTC KOMPASS ──────────────────────────── */}
      <section className="py-24 bg-[#050B1B]">
        <div className="container-sat max-w-3xl">
          <div className="text-center mb-10">
            <div className="overline mb-4">SAT-HTC Kompass</div>
            <h2 className="serif text-3xl md:text-4xl font-light leading-tight">
              Die sanfte Einführung in den Human Transformation Code.
            </h2>
            <p className="text-white/65 mt-6 text-lg leading-relaxed">
              Eine kostenlose Kurzfassung als PDF. Kein Newsletter-Zwang, keine Bindung —
              nur ein klarer erster Impuls, wenn du wissen möchtest, wovon SAT wirklich spricht.
            </p>
          </div>
          <HtcLeadMagnetForm />
          <p className="text-white/35 text-xs mt-8 italic text-center">
            Das vollständige SAT-HTC-Gesamtwerk ist SAT MASTER Teilnehmern vorbehalten.
          </p>
        </div>
      </section>

      {/* ── FINAL CTA ────────────────────────────────────────────── */}
      <section className="py-28 bg-[#081328]">
        <div className="container-sat max-w-3xl text-center">
          <Heart size={28} className="text-[#D4AF37] mx-auto mb-8" />
          <h2 className="serif text-4xl md:text-6xl font-light leading-tight">
            Bereit, deinen Code zu erkennen?
          </h2>
          <p className="serif italic text-white/70 text-xl mt-8 leading-relaxed">
            Wenn du spürst, dass dein Leben mehr ist als Zufall, beginnt hier dein nächster Schritt.
          </p>
          <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/sat-master" className="btn-primary pulse-gold" data-testid="htc-final-master">
              SAT MASTER entdecken <ArrowRight size={16} />
            </Link>
            <Link to="/mentor" className="btn-secondary" data-testid="htc-final-mentor">
              SAT MENTOR werden
            </Link>
            <Link to="/erstgespraech" className="btn-ghost" data-testid="htc-final-contact">
              Kontakt aufnehmen
            </Link>
          </div>
          <div className="mt-16 serif italic text-[#D4AF37] text-lg">
            „Love Is Transformation.&ldquo;
          </div>
        </div>
      </section>
    </div>
  );
}
